import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {message} from 'antd';
import {AxiosError, AxiosResponse} from 'axios';
import employeeService from './employeeServices';
import type {ADD_EMPLOYEE, UPDATE_EMP, USERDATA} from './employees.type';
import type FILTERS from '../../../../common/filters.type';

const initialState = {
  isLoading: false,
  employees: {
    page_size: 10,
    page_number: 1,
    sort_by: 'name',
    ascending: true,
    search: '',
    totalDataCount: 0,
    is_active: undefined as boolean | undefined,
    pageData: [] as USERDATA[],
  },
};

export const employeesList = createAsyncThunk(
  'employeeList',
  async (payload: FILTERS, thunkAPI) => {
    try {
      const data: AxiosResponse = await employeeService.employeesList(payload);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const addEmployee = createAsyncThunk(
  'addEmployee',
  async (values: ADD_EMPLOYEE, thunkAPI) => {
    try {
      const data: AxiosResponse = await employeeService.addEmployee(values);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);
export const updateEmployee = createAsyncThunk(
  'updateEmployee',
  async (
    values: {
      payload: UPDATE_EMP;
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      const data: AxiosResponse = await employeeService.updateEmployee({
        payload: values.payload,
        id: values.id,
      });

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const employeesSlice = createSlice({
  name: 'employees',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    pageNumberChange(state, action: PayloadAction<number>) {
      state.employees.page_number = action.payload;
    },
    pageSizeChange(state, action: PayloadAction<number>) {
      state.employees.page_number = 1;
      state.employees.page_size = action.payload;
    },
    searchBy(state, action: PayloadAction<string>) {
      state.employees.page_number = 1;
      state.employees.search = action.payload;
    },
    clearEmployeesFilters(state) {
      state.employees.page_size = 10;
      state.employees.page_number = 1;
      state.employees.search = '';
      state.employees.sort_by = 'name';
      state.employees.is_active = undefined;
    },
    employeesStatusFilter(state, action: PayloadAction<boolean | undefined>) {
      state.employees.page_number = 1;
      state.employees.is_active = action.payload;
    },
    sortBy(state, action: PayloadAction<{ascending: boolean; sortBy: string}>) {
      state.employees.sort_by = action.payload.sortBy;
      state.employees.ascending = action.payload.ascending;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(employeesList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(employeesList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.employees = action.payload.data;
    });
    builder.addCase(employeesList.rejected, (state, action) => {
      state.isLoading = false;
      state.employees = initialState.employees;
      message.error(`${action.payload}`);
    });

    builder.addCase(addEmployee.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addEmployee.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`User added Successfully`);
    });
    builder.addCase(addEmployee.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });

    builder.addCase(updateEmployee.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateEmployee.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`User updated Successfully`);
    });
    builder.addCase(updateEmployee.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });
  },
});
export const {
  pageNumberChange,
  pageSizeChange,
  searchBy,
  clearEmployeesFilters,
  employeesStatusFilter,
  sortBy,
} = employeesSlice.actions;

export default employeesSlice.reducer;
